import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Chip, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useMemo } from "react";
import { NotificationTableRow_notification$data } from "./__generated__/NotificationTableRow_notification.graphql";

type StatusProps = {
  label: string;
  tooltip: string | null;
  color: React.ComponentProps<typeof Chip>["color"];
};

type Props = {
  notification: NotificationTableRow_notification$data;
};

const NotificationStatusPill = ({ notification }: Props) => {
  const props = useMemo((): StatusProps => {
    const automaticArchiveTimestamp =
      notification.automaticArchiveTimestamp ?? null;
    const publishedTime = notification.timestamp;
    const isScheduled = dayjs(publishedTime).isAfter(dayjs());
    const hasAutoArchiveTime = automaticArchiveTimestamp !== null;
    const isAutoArchived =
      hasAutoArchiveTime && dayjs().isAfter(dayjs(automaticArchiveTimestamp));
    const isArchived = notification.isArchived;

    if (isArchived) {
      return {
        label: "Archived",
        tooltip: "",
        color: "info",
      };
    } else if (notification.confirmationStatus === "pending") {
      return {
        label: "AI Suggestion",
        tooltip: "",
        color: "primary",
      };
    } else if (isAutoArchived) {
      const tooltip =
        "Auto archived at " +
        dayjs(automaticArchiveTimestamp).format("llll z") +
        ", " +
        dayjs(automaticArchiveTimestamp).from(publishedTime, true) +
        " after publish time";
      return {
        label: "Archived",
        tooltip,
        color: "info",
      };
    } else if (isScheduled) {
      const tooltip = hasAutoArchiveTime
        ? "Will auto archive " +
          dayjs(automaticArchiveTimestamp).from(publishedTime, true) +
          " after publish time"
        : "";
      return {
        label: "Scheduled",
        tooltip,
        color: "secondary",
      };
    } else {
      const tooltip = hasAutoArchiveTime
        ? "Will auto archive " +
          dayjs(automaticArchiveTimestamp).from(publishedTime)
        : "";
      return {
        label: "Published",
        tooltip,
        color: "success",
      };
    }
  }, [notification]);

  return (
    <Tooltip title={props.tooltip} placement="top" arrow>
      <Chip
        icon={
          props.tooltip === "" ? undefined : (
            <InfoOutlinedIcon sx={{ color: "white" }} fontSize="small" />
          )
        }
        label={props.label}
        color={props.color}
      />
    </Tooltip>
  );
};

export default NotificationStatusPill;
